import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Trash2, Minus, Plus, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { resolveCartDiscount } from "@/lib/discounts";

export default function Cart() {
  const { items, removeItem, updateQuantity, subtotal } = useCart();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [sale, setSale] = useState(null);

  useEffect(() => {
    apiClient.get("/sale/status").then(({ data }) => setSale(data)).catch(() => setSale({ active: false }));
  }, []);

  const applyCoupon = async () => {
    setCouponError("");
    try {
      const { data } = await apiClient.post("/coupons/apply", { code: couponCode, subtotal });
      setCoupon(data);
    } catch (e) {
      setCoupon(null);
      setCouponError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const { discount, label: discountLabel, useSale, useQtyDiscount } = resolveCartDiscount({ items, subtotal, coupon, sale });
  const freeShipping = Boolean(coupon?.free_shipping);
  const shipping = freeShipping ? 0 : subtotal >= 150 || subtotal === 0 ? 0 : 12;
  const total = subtotal - discount + shipping;

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-28 text-center" data-testid="cart-empty-state">
        <ShoppingBag className="h-12 w-12 text-slate-300 mx-auto mb-5" strokeWidth={1.5} />
        <h1 className="text-2xl font-display font-medium text-slate-900">Your Cart is Empty</h1>
        <p className="text-sm text-slate-500 mt-2">Browse our research compounds to get started.</p>
        <Link to="/shop"><Button className="mt-6 rounded-full btn-lift" data-testid="cart-empty-shop-btn">Browse Shop</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="cart-page">
      <h1 className="text-3xl font-display font-medium text-slate-900 mb-10">Shopping Cart</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.product_id} className="flex items-center gap-4 border border-slate-200 rounded-2xl p-4" data-testid={`cart-item-${item.product_id}`}>
              <Link to={`/product/${item.slug}`} className="flex-1">
                <p className="font-display font-semibold text-sm text-slate-900">{item.name}</p>
                <p className="text-xs text-slate-500">{item.strength} &middot; Batch {item.batch_number}</p>
              </Link>
              <div className="flex items-center border border-slate-200 rounded-full">
                <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="p-2 hover:text-brandblue-600 transition-colors" data-testid={`cart-decrease-${item.product_id}`}><Minus className="h-3.5 w-3.5" /></button>
                <span className="px-2 text-sm font-semibold" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="p-2 hover:text-brandblue-600 transition-colors" data-testid={`cart-increase-${item.product_id}`}><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <span className="font-semibold text-slate-900 w-20 text-right" data-testid={`cart-item-total-${item.product_id}`}>${(item.price * item.quantity).toFixed(2)}</span>
              <button onClick={() => removeItem(item.product_id)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${item.product_id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <p className="text-xs text-slate-500 leading-relaxed pt-2" data-testid="cart-ruo-notice">
            All products sold by True Impact Peptides are intended solely for lawful laboratory research purposes.{" "}
            <Link to="/research-use-disclaimer" target="_blank" rel="noopener noreferrer" className="text-brandblue-600 font-semibold hover:text-brandblue-800 transition-colors" data-testid="cart-ruo-disclaimer-link">
              Research Use Only Disclaimer
            </Link>
          </p>
        </div>

        <div className="border border-slate-200 rounded-2xl p-6 h-fit sticky top-24">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-5">Order Summary</h2>
          <div className="flex gap-2 mb-4">
            <Input placeholder="Coupon code" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} data-testid="cart-coupon-input" />
            <Button variant="outline" onClick={applyCoupon} data-testid="cart-apply-coupon-btn">Apply</Button>
          </div>
          {couponError && <p className="text-xs text-red-500 mb-3" data-testid="cart-coupon-error">{couponError}</p>}
          {coupon && freeShipping && <p className="text-xs text-emerald-600 mb-3" data-testid="cart-free-shipping-applied">Code "{coupon.code}" applied — free shipping! This stacks on top of any other discount.</p>}
          {coupon && !freeShipping && !useQtyDiscount && !useSale && <p className="text-xs text-emerald-600 mb-3" data-testid="cart-coupon-success">Coupon "{coupon.code}" applied</p>}
          {coupon && !freeShipping && useQtyDiscount && <p className="text-xs text-amber-600 mb-3" data-testid="cart-coupon-superseded">Your quantity discount saves more than "{coupon.code}", so it was applied instead. Discounts do not stack.</p>}
          {coupon && !freeShipping && useSale && <p className="text-xs text-amber-600 mb-3" data-testid="cart-coupon-superseded-sale">The Summer Sale saves as much or more than "{coupon.code}", so it was applied instead. Discounts do not stack.</p>}
          {useQtyDiscount && !coupon && <p className="text-xs text-emerald-600 mb-3" data-testid="cart-qty-discount-applied">Same-product quantity discount applied automatically</p>}
          {useSale && (!coupon || freeShipping) && <p className="text-xs text-emerald-600 mb-3" data-testid="cart-sale-applied">Summer Sale: {sale.percent}% off applied automatically</p>}

          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-600"><span>{discountLabel}</span><span data-testid="cart-discount">-${discount.toFixed(2)}</span></div>}
            <div className="flex justify-between text-slate-600"><span>Shipping</span><span data-testid="cart-shipping">{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between font-display font-bold text-slate-900 text-base pt-3 border-t border-slate-100"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
            <p className="text-[11px] text-slate-400 mt-1.5" data-testid="cart-tax-note">Sales tax is calculated at checkout based on your state.</p>
          </div>

          <Button
            size="lg"
            className="w-full mt-6 rounded-full btn-lift"
            onClick={() => navigate("/checkout", { state: { coupon: freeShipping ? coupon : useQtyDiscount || useSale ? null : coupon } })}
            data-testid="proceed-to-checkout-btn"
          >
            Proceed to Checkout <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
